import { useEffect, useState } from "react";
import httpService from "../../services/httpService";
import Button from 'react-bootstrap/Button';
import Badge from "react-bootstrap/Badge";
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {
    FaEnvelope, FaClock, FaCheckCircle, FaTimesCircle,
} from "react-icons/fa";
import LoadingFade from "../LoadingFade";
import "../../styles/ClubsToJoin.scss";

const ListenerInvites = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [invites, setInvites] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pagination, setPagination] = useState({
        total: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false,
    });

    const fetchInvites = () => {
        setLoading(true);
        httpService.get("/dash/get-invites", {
            params: { pageNumber, pageSize, ...(statusFilter && { status: statusFilter }) },
        })
            .then((res) => {
                setInvites(res.data.data);
                setPagination(res.data.pagination);
            })
            .catch((err) => setError(err.response?.data?.message || "Error loading invites"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchInvites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize, statusFilter]);

    const statusBadge = (invite) => {
        if (invite.is_expired)  return <Badge bg="secondary">Expired</Badge>;
        if (invite.is_accepted) return <Badge bg="success">Accepted</Badge>;
        if (invite.is_pending)  return <Badge bg="warning" text="dark">Pending</Badge>;
        if (invite.status === 'declined') return <Badge bg="danger">Declined</Badge>;
        return <Badge bg="info">{invite.status}</Badge>;
    };

    const statusIcon = (invite) => {
        if (invite.is_expired)  return <FaClock className="text-secondary" />;
        if (invite.is_accepted) return <FaCheckCircle className="text-success" />;
        if (invite.status === 'declined') return <FaTimesCircle className="text-danger" />;
        return <FaEnvelope style={{ color: '#FACE68' }} />;
    };

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

    return (
        <LoadingFade loading={loading} stateKey={loading ? 'loading' : 'loaded'}>
            <div>
                {/* ── Toolbar ── */}
                <Row className="mb-4 align-items-center g-2">
                    <Col md={4}>
                        <Form.Select
                            size="sm"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPageNumber(1); }}
                            style={{ width: 'auto' }}
                        >
                            <option value="">All Invites</option>
                            <option value="pending">Pending</option>
                            <option value="accepted">Accepted</option>
                            <option value="declined">Declined</option>
                        </Form.Select>
                    </Col>
                    <Col md={8} className="d-flex justify-content-end align-items-center gap-2">
                        <span className="text-muted small">Per page:</span>
                        <Form.Select
                            size="sm"
                            value={pageSize}
                            onChange={(e) => { setPageSize(parseInt(e.target.value)); setPageNumber(1); }}
                            style={{ width: 'auto' }}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                        </Form.Select>
                    </Col>
                </Row>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* ── Invite cards ── */}
                {invites.length > 0 ? (
                    <>
                        {invites.map((invite) => (
                            <div key={invite.id} className="invite-card card">
                                <div className="card-body p-4">

                                    {/* Top row: icon + club title + status badge */}
                                    <div className="invite-card__top">
                                        <div className="d-flex align-items-start gap-3">
                                            <div className="invite-card__icon">
                                                {statusIcon(invite)}
                                            </div>
                                            <div>
                                                <div className="invite-card__club">
                                                    {invite.podcast_club?.title || 'Podcast Club Invite'}
                                                </div>
                                                {invite.email_subject && (
                                                    <div className="invite-card__subject">{invite.email_subject}</div>
                                                )}
                                            </div>
                                        </div>
                                        {statusBadge(invite)}
                                    </div>

                                    {/* Meta row */}
                                    <div className="invite-card__meta">
                                        <span>
                                            <strong>From:</strong>{' '}
                                            {invite.inviter?.first_name} {invite.inviter?.last_name}
                                        </span>
                                        {fmtDate(invite.created_at) && (
                                            <span><strong>Sent:</strong> {fmtDate(invite.created_at)}</span>
                                        )}
                                        {invite.expires_at && (
                                            <span><strong>Expires:</strong> {fmtDate(invite.expires_at)}</span>
                                        )}
                                        {invite.accepted_at && (
                                            <span><strong>Accepted:</strong> {fmtDate(invite.accepted_at)}</span>
                                        )}
                                        {invite.access_code && (
                                            <span><strong>Code:</strong> <code>{invite.access_code}</code></span>
                                        )}
                                    </div>

                                    {/* Invite message */}
                                    {invite.content && (
                                        <div className="invite-card__content">{invite.content}</div>
                                    )}

                                    {/* Actions */}
                                    {invite.is_pending && !invite.is_expired && (
                                        <div className="invite-card__actions">
                                            <Button variant="primary" size="sm">
                                                Accept Invite
                                            </Button>
                                            <Button variant="outline-secondary" size="sm">
                                                Decline
                                            </Button>
                                        </div>
                                    )}

                                </div>
                            </div>
                        ))}

                        {/* Pagination */}
                        <div className="ctj-pagination">
                            <span>
                                Showing {(pageNumber - 1) * pageSize + 1}–{Math.min(pageNumber * pageSize, pagination.total)} of {pagination.total} invites
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-primary" size="sm"
                                    onClick={() => setPageNumber(p => p - 1)}
                                    disabled={!pagination.hasPreviousPage}
                                >
                                    Previous
                                </Button>
                                <span>Page {pageNumber} of {pagination.totalPages}</span>
                                <Button
                                    variant="outline-primary" size="sm"
                                    onClick={() => setPageNumber(p => p + 1)}
                                    disabled={!pagination.hasNextPage}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="ctj-empty">
                        <FaEnvelope className="ctj-empty__icon" />
                        <p className="ctj-empty__text">No invites found.</p>
                    </div>
                )}
            </div>
        </LoadingFade>
    );
};

export default ListenerInvites;
